define('businessServices/router/plugins/accountStatusEvaluatorRouterPlugin',[
    'businessServices/login/accountStatusEvaluator',
    'settings/navigationConfiguration',
    'common/promises/promiseFactory',
    'businessServices/authentication/sessionAccountInfo',
    'businessServices/authentication/sessionWorkflowFlags',
    'businessServices/onboarding/liveReceptionistOnboardingSingleton',
    'constants/liveReceptionistOnboardingStatusConstants',
    'persistence/repositories/liveReceptionistOnboardingRepository'
], function (accountStatusEvaluator, navigationConfiguration, PromiseFactoryConstructor, sessionAccountInfo, sessionWorkflowFlags) {
    const _accountStatusEvaluator = accountStatusEvaluator;
    const _navigationConfiguration = navigationConfiguration;
    const _promiseFactory = new PromiseFactoryConstructor();
    const _sessionAccountInfo = sessionAccountInfo;
    const _sessionWorkflowFlags = sessionWorkflowFlags;

    const LiveReceptionistOnboardingRepository = require('persistence/repositories/liveReceptionistOnboardingRepository');
    const _liveReceptionistOnboardingRepository = new LiveReceptionistOnboardingRepository();

    const _liveReceptionistOnboardingState = require('businessServices/onboarding/liveReceptionistOnboardingSingleton');
    const _liveReceptionistOnboardingStatus = require('constants/liveReceptionistOnboardingStatusConstants');

    const ROUTE_ID__LOGIN = _navigationConfiguration.routesById.login.routeId;

    const ROUTE_ID__PAY_BALANCE = _navigationConfiguration.routesById.payBalance.routeId;
    const ROUTE_ID__CLOSED_BALANCE_OWED = _navigationConfiguration.routesById.closedBalanceOwed.routeId;
    const ROUTE_ID__CLOSED_DO_NOT_REACTIVATE = _navigationConfiguration.routesById.closedDoNotReactivate.routeId;
    const ROUTE_ID__CLOSED_AND_CANNOT_REACTIVATE = _navigationConfiguration.routesById.closedAndCannotReactivate.routeId;
    const ROUTE_ID__MISCELLANEOUS_PROBLEMS = _navigationConfiguration.routesById.miscellaneousProblems.routeId;

    const ROUTE_ID__REACTIVATION = _navigationConfiguration.routesById.reactivation.routeId;
    const ROUTE_ID__REACTIVATION_PLANS = _navigationConfiguration.routesById.reactivationPlans.routeId;
    const ROUTE_ID__REACTIVATION_SUCCESS = _navigationConfiguration.routesById.reactivationSuccess.routeId;

    const ROUTE_ID__ONBOARDING_WELCOME = _navigationConfiguration.routesById.liveReceptionistOnboardingWelcome.routeId;
    const ROUTE_ID__ONBOARDING_SCHEDULING = _navigationConfiguration.routesById.liveReceptionistOnboardingScheduling.routeId;
    const ROUTE_ID__ONBOARDING_IDENTITY = _navigationConfiguration.routesById.liveReceptionistOnboardingIdentity.routeId;
    const ROUTE_ID__ONBOARDING_AVAILABILITY = _navigationConfiguration.routesById.liveReceptionistOnboardingAvailability.routeId;
    const ROUTE_ID__ONBOARDING_INFORMATION =  _navigationConfiguration.routesById.liveReceptionistOnboardingInformation.routeId;
    const ROUTE_ID__ONBOARDING_DELIVERY = _navigationConfiguration.routesById.liveReceptionistOnboardingDelivery.routeId;
    const ROUTE_ID__ONBOARDING_PROCESSING = _navigationConfiguration.routesById.liveReceptionistOnboardingProcessing.routeId;

    function isRouteSecure(routeId) {
        const navigationRouteInfo = _navigationConfiguration.routesById[routeId];
        return navigationRouteInfo.isSecure;
    }

    const ONBOARDING_ROUTE_IDS = [
        ROUTE_ID__ONBOARDING_WELCOME,
        ROUTE_ID__ONBOARDING_SCHEDULING,
        ROUTE_ID__ONBOARDING_IDENTITY,
        ROUTE_ID__ONBOARDING_AVAILABILITY,
        ROUTE_ID__ONBOARDING_INFORMATION,
        ROUTE_ID__ONBOARDING_DELIVERY,
        ROUTE_ID__ONBOARDING_PROCESSING
    ];

    function guardOnboardingRoute(routeId, deferredObject) {
        // Scheduling isn't tied to the epoch: the LCA wizard's "talk to a specialist" hand-off lands
        // here just after the epoch ends, and add-on services may be scheduled here later.
        if (routeId === ROUTE_ID__ONBOARDING_SCHEDULING) {
            deferredObject.resolve(true);
            return;
        }

        // The wizard is first-time setup only. After the epoch, new services are onboarded by a call.
        if (_accountStatusEvaluator.isWelcomeInfoCapture() === false) {
            deferredObject.resolve({routeUrl: _navigationConfiguration.homePageUrl});
            return;
        }

        _liveReceptionistOnboardingRepository.checkIsEligibleForOnboarding()
            .fail(deferredObject.reject)
            .done((response) => {
                if (response.isEligibleForOnboarding) {
                    _liveReceptionistOnboardingState.getLiveReceptionistOnboarding(true)
                        .fail(deferredObject.reject)
                        .done((liveReceptionistOnboarding) => {
                            const onboardingStatus = liveReceptionistOnboarding.onboardingStatus;
                            const isCompleted = onboardingStatus === _liveReceptionistOnboardingStatus.accountOnboardingCompleted;
                            const isFailedOrInProgress = (
                                onboardingStatus === _liveReceptionistOnboardingStatus.accountOnboardingFailed ||
                                onboardingStatus === _liveReceptionistOnboardingStatus.accountOnboardingFailedRetryQueued ||
                                onboardingStatus === _liveReceptionistOnboardingStatus.accountOnboardingInProgress ||
                                onboardingStatus === _liveReceptionistOnboardingStatus.accountOnboardingPending
                            );

                            if (routeId === ROUTE_ID__ONBOARDING_PROCESSING) {
                                if (isFailedOrInProgress) {
                                    deferredObject.resolve(true);
                                } else {
                                    deferredObject.resolve({routeUrl: _navigationConfiguration.homePageUrl});
                                }
                            } else if (isCompleted) {
                                deferredObject.resolve({routeUrl: _navigationConfiguration.homePageUrl});
                            } else if (isFailedOrInProgress) {
                                deferredObject.resolve({routeUrl: _navigationConfiguration.routesById.liveReceptionistOnboardingProcessing.url});
                            } else {
                                deferredObject.resolve(true);
                            }
                        });
                } else {
                    deferredObject.resolve({routeUrl: _navigationConfiguration.homePageUrl});
                }
            });
    }

    function routeByStatus(routeId) {
        if (_sessionAccountInfo.isLoggedIn() === false) {
            if (isRouteSecure(routeId)) {
                if (routeId !== ROUTE_ID__LOGIN) {
                    _sessionWorkflowFlags.setOriginalPathname(window.location.pathname);
                }
                return {routeUrl: _navigationConfiguration.loginPageUrl};
            } else {
                return true;
            }
        } else {
            return _promiseFactory.defer(function (deferredObject) {
                _accountStatusEvaluator.evaluate()
                    .fail(() => {
                        deferredObject.resolve(true);
                    })
                    .done(function (evaluation) {
                        switch (evaluation) {
                            case _accountStatusEvaluator.evaluateResponses.closedNoBalance:
                                if (routeId === ROUTE_ID__REACTIVATION  ||
                                    routeId === ROUTE_ID__REACTIVATION_PLANS ||
                                    routeId === ROUTE_ID__REACTIVATION_SUCCESS) {
                                    deferredObject.resolve(true);
                                } else {
                                    deferredObject.resolve({routeUrl: _navigationConfiguration.reactivation});
                                }
                                break;
                            case _accountStatusEvaluator.evaluateResponses.normal:
                                if (routeId === ROUTE_ID__LOGIN && _sessionAccountInfo.userDeviceIsMobilePhone() === true) {
                                    deferredObject.resolve(true);
                                } else if (routeId === ROUTE_ID__REACTIVATION ||
                                    routeId === ROUTE_ID__REACTIVATION_PLANS ||
                                    routeId === ROUTE_ID__LOGIN ||
                                    routeId === ROUTE_ID__PAY_BALANCE ||
                                    routeId === ROUTE_ID__CLOSED_BALANCE_OWED ||
                                    routeId === ROUTE_ID__CLOSED_DO_NOT_REACTIVATE ||
                                    routeId === ROUTE_ID__CLOSED_AND_CANNOT_REACTIVATE ||
                                    routeId === ROUTE_ID__MISCELLANEOUS_PROBLEMS) {
                                    deferredObject.resolve({routeUrl: _navigationConfiguration.homePageUrl});
                                } else if (ONBOARDING_ROUTE_IDS.includes(routeId)) {
                                    guardOnboardingRoute(routeId, deferredObject);
                                } else {
                                    deferredObject.resolve(true);
                                }
                                break;
                            case _accountStatusEvaluator.evaluateResponses.pastDueBalance:
                                if (routeId === ROUTE_ID__PAY_BALANCE) {
                                    deferredObject.resolve(true);
                                } else {
                                    deferredObject.resolve({routeUrl: _navigationConfiguration.payBalance});
                                }
                                break;
                            case _accountStatusEvaluator.evaluateResponses.miscellaneousProblems:
                                if (routeId === ROUTE_ID__MISCELLANEOUS_PROBLEMS) {
                                    deferredObject.resolve(true);
                                } else {
                                    deferredObject.resolve({routeUrl: _navigationConfiguration.miscellaneousProblems});
                                }
                                break;
                            case _accountStatusEvaluator.evaluateResponses.closedBalanceOwed:
                                if (routeId === ROUTE_ID__CLOSED_BALANCE_OWED  ||
                                    routeId === ROUTE_ID__REACTIVATION_PLANS ||
                                    routeId === ROUTE_ID__REACTIVATION_SUCCESS) {
                                    deferredObject.resolve(true);
                                } else {
                                    deferredObject.resolve({routeUrl: _navigationConfiguration.closedBalanceOwed});
                                }
                                break;
                            case _accountStatusEvaluator.evaluateResponses.closedDoNotReactivate:
                                if (routeId === ROUTE_ID__CLOSED_DO_NOT_REACTIVATE) {
                                    deferredObject.resolve(true);
                                } else {
                                    deferredObject.resolve({routeUrl: _navigationConfiguration.closedDoNotReactivate});
                                }
                                break;
                            case _accountStatusEvaluator.evaluateResponses.closedAndCannotReactivate:
                                if (routeId === ROUTE_ID__CLOSED_AND_CANNOT_REACTIVATE) {
                                    deferredObject.resolve(true);
                                } else {
                                    deferredObject.resolve({routeUrl: _navigationConfiguration.closedAndCannotReactivate});
                                }
                                break;
                            default:
                                if (routeId === ROUTE_ID__LOGIN) {
                                    deferredObject.resolve(true);
                                } else {
                                    deferredObject.resolve({routeUrl: _navigationConfiguration.loginPageUrl});
                                }
                                break;
                        }
                    });
            });
        }
    }

    return {
        guardRoute: routeByStatus
    };
});

